import type {
	BrowserClient,
	Breadcrumb as PublicBreadcrumb,
	BrowserOptions as PublicBrowserOptions,
	Breadcrumb as PrivateBreadcrumb,
	Event,
	EventHint,
} from '@sentry/browser';

import {
	subscribeToRequestObservations,
	type RequestObservation,
} from '@atlassian/conversation-assistant-utils/request-observer';

import { isClientFetchError } from '../../common/utils/is-error';

import { ConversationAssistantBreadcrumbGateway } from './breadcrumb-gateway';
import { normaliseUrl, normalizeUUID } from './utils';

const MAX_LINKED_ERRORS = 5;
const MAX_MATCHED_UUID_TAG_LENGTH = 200;
const BLOCKED_LOCATION_PARTS = [
	'localhost',
	'jira-dev.com',
	'stg.atlassian.com',
	'stg.internal.atlassian.com',
	'stg-east.frontend.public.atl-paas.net',
];

type SentryModule = {
	BrowserClient: typeof import('@sentry/browser').BrowserClient;
	eventFromException: (
		exception: unknown,
		hint?: EventHint,
		attachStacktrace?: boolean,
	) => PromiseLike<Event>;
};

type PrivateReporterOptions = {
	accountId: string;
	dsn: string;
	environment: string | undefined;
	ignoreErrors?: PublicBrowserOptions['ignoreErrors'];
	packageName: string;
	packageVersion?: string;
	product: string;
	release?: string;
	sentry: SentryModule;
};

type CaptureExceptionOptions = {
	exception: unknown;
	name: string;
	tags?: Record<string, string | undefined>;
};

const eventId = (): string => crypto.randomUUID().replace(/-/g, '');

const matchesIgnorePattern = (
	value: string,
	patterns: PublicBrowserOptions['ignoreErrors'],
): boolean =>
	patterns?.some((pattern) =>
		typeof pattern === 'string'
			? value.includes(pattern)
			: (() => {
					pattern.lastIndex = 0;
					const matches = pattern.test(value);
					pattern.lastIndex = 0;
					return matches;
				})(),
	) ?? false;

const isBlockedLocation = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}
	return BLOCKED_LOCATION_PARTS.some((part) => window.location.href.includes(part));
};

const requestObservationToBreadcrumb = (observation: RequestObservation): PublicBreadcrumb => ({
	category:
		observation.kind === 'graphql'
			? 'conversation-assistant.request.graphql'
			: 'conversation-assistant.request.http',
	data: observation,
	level: (observation.outcome === 'success' || observation.outcome === 'abort'
		? 'info'
		: 'warning') as PublicBreadcrumb['level'],
	message:
		observation.kind === 'graphql'
			? `${observation.operation} ${observation.outcome}`
			: `${observation.method} ${observation.route} ${observation.outcome}`,
	type: 'http',
});

const toPrivateBreadcrumb = (breadcrumb: PublicBreadcrumb): PrivateBreadcrumb => ({
	...breadcrumb,
	level: breadcrumb.level as PrivateBreadcrumb['level'],
});

const getCause = (error: Error): Error | undefined => {
	const cause = (error as Error & { cause?: unknown }).cause;
	return cause instanceof Error ? cause : undefined;
};

export class ConversationAssistantPrivateReporter {
	private readonly breadcrumbGateway = new ConversationAssistantBreadcrumbGateway();
	private readonly client: BrowserClient;
	private readonly enabled: boolean;
	private readonly ignoreErrors: PublicBrowserOptions['ignoreErrors'];
	private readonly sentry: SentryModule;
	private accountId: string;
	private packageName: string;
	private packageVersion: string | undefined;
	private product: string;
	private readonly unsubscribeFromRequestObservations: () => void;

	public constructor({
		accountId,
		dsn,
		environment,
		ignoreErrors,
		packageName,
		packageVersion,
		product,
		release,
		sentry,
	}: PrivateReporterOptions) {
		this.accountId = accountId;
		this.enabled = environment === 'production';
		this.ignoreErrors = ignoreErrors;
		this.packageName = packageName;
		this.packageVersion = packageVersion;
		this.product = product;
		this.sentry = sentry;
		this.client = new sentry.BrowserClient({
			attachStacktrace: true,
			autoSessionTracking: false,
			dsn,
			enabled: this.enabled,
			environment,
			integrations: [],
			maxBreadcrumbs: 100,
			release,
			sendClientReports: true,
		});
		this.unsubscribeFromRequestObservations = subscribeToRequestObservations((observation) => {
			this.breadcrumbGateway.add(requestObservationToBreadcrumb(observation));
		});
	}

	public updateContext({
		accountId,
		packageVersion,
		product,
	}: {
		accountId: string;
		packageVersion?: string;
		product: string;
	}): void {
		this.accountId = accountId;
		if (this.packageVersion === undefined && packageVersion !== undefined) {
			this.packageVersion = packageVersion;
		}
		this.product = product;
	}

	public addBreadcrumb(crumb: string | PublicBreadcrumb): void {
		this.breadcrumbGateway.add(crumb);
	}

	public async captureException({ exception, name, tags }: CaptureExceptionOptions): Promise<void> {
		if (!this.enabled || isClientFetchError(exception) || isBlockedLocation()) {
			return;
		}

		const rootMessage = exception instanceof Error ? exception.message : String(exception);
		if (matchesIgnorePattern(rootMessage, this.ignoreErrors)) {
			return;
		}

		const id = eventId();
		const event = await this.sentry.eventFromException(exception, { event_id: id }, true);
		await this.addLinkedErrors(event, exception);

		const eventTags: Record<string, string | undefined> = {
			experience: tags?.source === 'aiMateExperienceTracker' ? name : undefined,
			name,
			packageName: this.packageName,
			packageVersion: this.packageVersion,
			product: this.product,
			reportingPipeline: 'isolatedPrivateV1',
			sentryOwner: 'conversation-assistant',
			...tags,
		};
		event.tags = {
			...event.tags,
			...eventTags,
		};
		event.user = { id: this.accountId };
		event.breadcrumbs = this.breadcrumbGateway.snapshot().map(toPrivateBreadcrumb);
		if (typeof window !== 'undefined') {
			event.request = {
				...event.request,
				url: normaliseUrl(window.location.href),
			};
		}

		this.normalizeEventUUIDs(event);

		// Do not pass `originalException`: BaseClient otherwise consults the SDK-wide
		// already-caught registry shared through Sentry's global carrier.
		this.client.captureEvent(event, { event_id: id });
	}

	public resetForTests(): void {
		this.unsubscribeFromRequestObservations();
		this.breadcrumbGateway.reset();
	}

	private async addLinkedErrors(event: Event, exception: unknown): Promise<void> {
		if (!(exception instanceof Error) || !event.exception?.values) {
			return;
		}

		const linkedValues: NonNullable<NonNullable<Event['exception']>['values']> = [];
		let current = getCause(exception);
		const seen = new Set<Error>([exception]);
		while (current && linkedValues.length < MAX_LINKED_ERRORS - 1 && !seen.has(current)) {
			seen.add(current);
			const linkedEvent = await this.sentry.eventFromException(current, undefined, true);
			if (linkedEvent.exception?.values?.[0]) {
				linkedValues.unshift(linkedEvent.exception.values[0]);
			}
			current = getCause(current);
		}
		event.exception.values = [...linkedValues, ...event.exception.values];
	}

	private normalizeEventUUIDs(event: Event): void {
		if (!event.exception?.values) {
			return;
		}

		const matchedUUIDs = new Set<string>();
		event.exception.values = event.exception.values.map((exceptionValue) => {
			const { normalized, matchedUUIDs: matches } = normalizeUUID(exceptionValue.value);
			matches.forEach((match) => matchedUUIDs.add(match));
			return {
				...exceptionValue,
				stacktrace: exceptionValue.stacktrace
					? {
							...exceptionValue.stacktrace,
							frames: exceptionValue.stacktrace.frames?.map((frame) => {
								const normalizedFrame = { ...frame };
								(['abs_path', 'filename', 'function', 'module'] as const).forEach((key) => {
									if (typeof normalizedFrame[key] !== 'string') {
										return;
									}
									const normalizedStackValue = normalizeUUID(normalizedFrame[key]);
									normalizedStackValue.matchedUUIDs.forEach((match) => matchedUUIDs.add(match));
									normalizedFrame[key] = normalizedStackValue.normalized;
								});
								return normalizedFrame;
							}),
						}
					: undefined,
				value: normalized,
			};
		});

		if (matchedUUIDs.size === 0) {
			return;
		}

		const boundedMatches: string[] = [];
		for (const match of matchedUUIDs) {
			const candidate = [...boundedMatches, match];
			if (JSON.stringify(candidate).length > MAX_MATCHED_UUID_TAG_LENGTH) {
				break;
			}
			boundedMatches.push(match);
		}
		event.tags = {
			...event.tags,
			matchedUUID: JSON.stringify(boundedMatches),
		};
	}
}
